import { useEffect, useRef } from "react";
import { CASE_DETAIL_FIELDS_WITHOUT_ITEMS, fetchCase } from "../../../api/case";
import {
  rememberUnreadMailCleared,
  stripUnreadEmailTags,
  tagsIncludeUnreadEmail,
  wasUnreadMailCleared,
} from "./unreadInboundMail";

function caseRecordFromFetch(res) {
  if (res?.id) return res;
  if (res?.data?.id) return res.data;
  return null;
}

/**
 * Opening a case clears Ulest svar fra kunde on the API via GET `/cases/:id`.
 * caseView already loads the case; this hook is for the list drawer peek.
 */
export function useClearUnreadEmailOnOpen(caseItem, { enabled = true, onCleared } = {}) {
  const caseId = caseItem?.id;
  const shouldClear = Boolean(enabled && caseId && tagsIncludeUnreadEmail(caseItem));
  const startedRef = useRef(null);
  const caseItemRef = useRef(caseItem);
  const onClearedRef = useRef(onCleared);
  caseItemRef.current = caseItem;
  onClearedRef.current = onCleared;

  useEffect(() => {
    if (!shouldClear) return undefined;
    if (wasUnreadMailCleared(caseId)) {
      onClearedRef.current?.(stripUnreadEmailTags(caseItemRef.current));
      return undefined;
    }
    if (startedRef.current === String(caseId)) return undefined;
    startedRef.current = String(caseId);

    let cancelled = false;
    fetchCase(caseId, { fields: CASE_DETAIL_FIELDS_WITHOUT_ITEMS })
      .then((res) => {
        if (cancelled) return;
        const record = caseRecordFromFetch(res);
        if (!record) {
          startedRef.current = null;
          return;
        }
        rememberUnreadMailCleared(caseId);
        const current = caseItemRef.current;
        onClearedRef.current?.(stripUnreadEmailTags({ ...current, ...record }));
      })
      .catch(() => {
        if (!cancelled) startedRef.current = null;
      });

    return () => {
      cancelled = true;
      if (!wasUnreadMailCleared(caseId)) startedRef.current = null;
    };
  }, [caseId, shouldClear]);
}

/** @deprecated use useClearUnreadEmailOnOpen */
export function useMarkUnreadMailRead(caseItem, options) {
  return useClearUnreadEmailOnOpen(caseItem, options);
}
